import type {
  FilterParamsHotels,
  SearchParams,
  SortParams,
} from '@/modules/common/context/QueryParams/models';
import type {TrackingEvent as GenericTrackingEvent} from '@/redux/middleware/googleTagManager/types';
import type {GetState} from '@/redux/typesNew';
import type {
  Hotel,
  HotelsResponse,
  RawHotel,
  RawHotelsResponse,
} from '@/redux/v1/hotels/list/models';

import {v4 as uuid} from '@lukeed/uuid';
import {ASYNC_TYPES} from '@travel-frontend/state-handling-sdk';
import {getCurrentUserId} from '@travel-frontend/user-id';
import axios from 'axios';
import Cookies from 'js-cookie';
import {isEmpty, isPlainObject, mapValues, pick} from 'lodash';

import {FILTER_PARAMS_HOTELS} from '@/modules/common/context/QueryParams/models';
import {combineHotelParamsToBackendQuery} from '@/modules/common/utils/combine';
import {reduxStore} from '@/redux/config';
import {
  FETCH_ALL_HOTELS,
  FETCH_HOTELS,
  FETCH_NEXT_HOTELS,
} from '@/redux/v1/hotels/list/actionTypes';
import {
  getCrossBordersData,
  getMetaUnfiltered,
  selectAreAllHotelsLoaded,
  selectPage,
  selectPageSize,
  selectTranslations,
} from '@/redux/v1/hotels/list/reducer';
import {resolveDestinationId} from '@/redux/v1/hotels/utils';
import {mergeMeta} from '@/redux/v1/utils/mergeMeta';
import {cyrb53} from '@/utils/cyrb53';

const hash = (params: Parameters<typeof JSON.stringify>[0]) => {
  return `${cyrb53(JSON.stringify(params))}`;
};

const mapRawHotel = ({
  tmpTotalPrice,
  tmpTotalPriceOriginal,
  tmpTotalDiscount,
  ...rawHotel
}: RawHotel): Hotel => {
  return {
    ...rawHotel,
    totalPrice: tmpTotalPrice,
    totalPriceOriginal: tmpTotalPriceOriginal,
    totalDiscount: tmpTotalDiscount,
    rating: rawHotel.rating ?? null,
  };
};

// translations are lifted to one top-level property, add them back to each category/variation for easier access
const mapMeta = (meta: any, translations: Record<string, string>) => {
  const shallowCopy = {...meta};

  if (meta.attributes) {
    shallowCopy.attributes = mapValues(
      meta.attributes,
      (group, attributesKey) => ({
        ...group,
        translation: translations[attributesKey],
        items: mapValues(group.items, (item, itemKey) => ({
          ...item,
          translation: translations[itemKey],
        })),
      })
    );
  }

  if (meta.tourOperatorSpecials) {
    shallowCopy.tourOperatorSpecials = mapValues(
      meta.tourOperatorSpecials,
      (item) => ({
        ...item,
        translation: translations[item.type],
        variations: item.variations?.map((variation: any) => ({
          ...variation,
          translation: translations[variation.type],
        })),
      })
    );
  }

  return shallowCopy;
};

export type FetchHotelsImplementationParams = {
  params: {
    searchParams: SearchParams;
    filterParams: any;
    sortParams: null | SortParams;
    offset: number;
    limit: number | null;
    initial: boolean;
    sponsoredAdPositions?: Array<number>;
  };
  abortSignal?: AbortSignal;
};

export const fetchHotelsImplementation = async ({
  params: {
    searchParams,
    filterParams,
    sortParams,
    offset,
    limit,
    initial,
    sponsoredAdPositions,
  },
  abortSignal,
}: FetchHotelsImplementationParams): Promise<HotelsResponse> => {
  const {data: rawData} = await axios.get<RawHotelsResponse>(
    `${
      import.meta.env.VITE_SERVICE_BFF_URL
    }/hotels?${combineHotelParamsToBackendQuery({
      searchParams,
      filterParams,
      sortParams,
      otherParams: {
        offset,
        limit,
      },
    })}`,
    {
      params: {
        initial,
        BD4ID: Cookies.get('bd4ti') || '',
        sponsoredHotels: sponsoredAdPositions?.join(','),
      },
      headers: {
        'X-User-Id': getCurrentUserId(),
      },
      signal: abortSignal,
    }
  );
  const rawHotels = Array.isArray(rawData.hotels) ? rawData.hotels : [];
  const initialMeta = isPlainObject(rawData.initialMeta)
    ? rawData.initialMeta
    : {};
  const meta =
    // BFF may return meta data, even without corresponding hotels.
    // This is a feature of some filters indicating the meta data that would apply
    // if the last applied filter was to be removed. Since it's not implemented
    // (and probably can't be) for all filters, we discard this meta data in that case
    // to make sure the ui stays consistent.
    isPlainObject(rawData.meta) && rawHotels.length > 0 ? rawData.meta : {};
  const translations =
    rawData.translations === undefined ? {} : rawData.translations;

  return {
    hotels: rawHotels.map((rawHotel, index) => {
      // track every time a sponsored hotel ad was delivered by the backend
      if (rawHotel.sponsored) {
        reduxStore.dispatch<GenericTrackingEvent>({
          type: 'TRACKING',
          payload: {
            event: 'impression',
            event_action: 'ad_loaded',
            ad_url: rawHotel.sponsored.impressionUrl,
            ad_position: `${index + 1}`,
            giata_id: rawHotel.id,
          },
        });
      }

      return mapRawHotel(rawHotel);
    }),
    crossBorders: rawData.crossBorders,
    meta,
    initialMeta,
    translations,
  };
};

let abortController: AbortController | null = null;

const createFetchHotelsAction = (spec: any) => {
  return (options: {
      initial?: boolean;
      searchParams: SearchParams;
      filterParams: FilterParamsHotels;
      sortParams: SortParams;
      forceNoLimit?: boolean;
    }) =>
    (dispatch: any, getState: GetState) => {
      const state = getState();
      const {
        type,
        initial = options.initial,
        searchParams = options.searchParams,
        filterParams = options.filterParams,
        sortParams = options.sortParams,
        // @ts-expect-error ...
        sponsoredAdPositions = options.sponsoredAdPositions,
        offset,
        limit,
        shouldCallAPI = () => true,
      } = spec({
        state,
        dispatch,
        options,
      });

      const hotelFilterParams = pick(filterParams, FILTER_PARAMS_HOTELS);

      if (abortController) {
        abortController.abort();
        abortController = new AbortController();
      } else {
        abortController = new AbortController();
      }

      return dispatch({
        types: ASYNC_TYPES(type),
        callAPI: (getAppState: GetState) => {
          const storedMetaUnfiltered = getMetaUnfiltered(getAppState());
          const storedCrossBordersData = getCrossBordersData(getAppState());
          const storedTranslations = selectTranslations(getAppState());

          const isInitialRequest =
            initial ||
            // initial request might yield empty unfiltered due to search params
            isEmpty(storedMetaUnfiltered);

          return resolveDestinationId(
            searchParams.destinationId,
            dispatch,
            getAppState
          )
            .then((resolvedDestinationId) =>
              fetchHotelsImplementation({
                params: {
                  searchParams: {
                    ...searchParams,
                    destinationId: resolvedDestinationId,
                  },
                  filterParams: hotelFilterParams,
                  sortParams,
                  offset,
                  limit,
                  initial: isInitialRequest,
                  sponsoredAdPositions,
                },
                abortSignal: abortController?.signal,
              })
            )
            .then(({hotels, meta, initialMeta, crossBorders, translations}) => {
              const nextId = hash({
                searchParams,
                sortParams,
                filterParams: hotelFilterParams,
              });
              const paramHash = hash({
                filterParams: hotelFilterParams,
                sortParams,
              });

              if (isInitialRequest) {
                const metaFiltered = mapMeta(meta, translations ?? {});
                return {
                  id: nextId,
                  paramHash,
                  hotels,
                  crossBorders,
                  metaUnfiltered: mergeMeta(
                    mapMeta(initialMeta, translations ?? {}),
                    metaFiltered
                  ),
                  metaFiltered,
                  translations,
                };
              }

              const metaFiltered = mapMeta(meta, {
                ...storedTranslations,
                ...translations,
              });

              return {
                id: nextId,
                paramHash,
                hotels,
                crossBorders: storedCrossBordersData,
                metaUnfiltered: mergeMeta(storedMetaUnfiltered, metaFiltered),
                metaFiltered,
                translations: storedTranslations,
              };
            });
        },
        shouldCallAPI,
        meta: {
          requestId: uuid(),
          initial: initial && !options.forceNoLimit,
        },
      });
    };
};

export const fetchHotels = createFetchHotelsAction(
  // @ts-expect-error ...
  ({state, options}) => ({
    type: FETCH_HOTELS,
    offset: 0,
    limit:
      !selectAreAllHotelsLoaded(state) ||
      (options.initial && !options.forceNoLimit)
        ? selectPageSize(state)
        : null,
  })
);

export const fetchNextHotels = createFetchHotelsAction(
  // @ts-expect-error ...
  ({state, dispatch}) => {
    const nextPage = selectPage(state) + 1;
    const limit = selectPageSize(state);
    const offset = (nextPage - 1) * limit;
    const areAllHotelsLoaded = selectAreAllHotelsLoaded(state);

    if (areAllHotelsLoaded) {
      dispatch({
        type: 'PAGE_CHANGED',
      });
    }

    return {
      type: FETCH_NEXT_HOTELS,
      initial: false,
      offset,
      limit,
      shouldCallAPI: () => !areAllHotelsLoaded,
    };
  }
);

export const fetchAllHotels = createFetchHotelsAction(() => {
  return {
    type: FETCH_ALL_HOTELS,
    initial: true,
    offset: 0,
    limit: null,
    // @ts-expect-error ...
    shouldCallAPI: (state) => !selectAreAllHotelsLoaded(state),
  };
});
